import React, {
  JSX,
  Suspense,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { Navigate, Outlet } from "react-router";
import { AxiosError } from "axios";
import { isAuthError, NoTokenError, useToken } from "@bn/api";
import {
  MandatorFeatures,
  MandatorType,
  useMandatorContext,
} from "@bn/theming";
import { useMyself } from "@api";
import { resolvePortalUrl } from "@helpers";
import { useMandatorFeature } from "@hooks";
import { AuthContext } from "@providers/AuthContext";
import { AppLayoutProvider } from "@providers";
import { AppLayout, LoadingProgressBar } from "@components";
import { useLoginRoute } from "@features/login/useLoginRoute";

export const ProtectedRoute = ({
  children,
  roles,
}: {
  children?: React.ReactNode;
  roles: string[];
}): React.ReactNode => {
  const { data: myself, isLoading } = useMyself();

  if (isLoading) {
    return null;
  }

  if (myself && !myself.roles?.some((role) => roles.includes(role))) {
    return <Navigate to="/" replace />;
  }

  return children ? children : <Outlet />;
};

export const OnlyAuthenticatedRoute = ({
  children,
}: {
  children?: React.ReactNode;
}): React.ReactNode => {
  const { setLoginRequired } = useContext(AuthContext);

  const handleTokenError = useCallback(
    (e: unknown) => {
      // eslint-disable-next-line no-console
      console.log(
        "only authenticated route error",
        e,
        isAuthError(e as NoTokenError | AxiosError<ApiException>)
      );
      if (!isAuthError(e as NoTokenError | AxiosError<ApiException>)) {
        return;
      }

      setLoginRequired(e as NoTokenError | AxiosError<ApiException>);
    },
    [setLoginRequired]
  );

  const { isFetching, isError } = useToken({
    onError: handleTokenError,
    key: "onlyAuthenticatedRoute",
  });

  if (isFetching || isError) {
    return null;
  }

  return children ? children : <Outlet />;
};

/**
 * Renders the route only for the given mandators. Mandator-specific pages are
 * hidden from the sidebar for everyone else, so a user of another mandator
 * reaching one has typed the URL directly — send them back to the dashboard.
 */
export const OnlyMandatorRoute = ({
  children,
  mandators,
}: {
  children?: React.ReactNode;
  mandators: MandatorType[];
}): React.ReactNode => {
  const { mandatorSlug } = useMandatorContext();

  if (!mandators.includes(mandatorSlug)) {
    return <Navigate to="/" replace />;
  }

  return children ? children : <Outlet />;
};

export const RouteWithRedirect = ({
  children,
  type,
  onlyOnProduction,
}: {
  children?: React.ReactNode;
  type: string;
  onlyOnProduction?: boolean;
}): React.ReactNode => {
  const { config } = useMandatorContext();

  if (
    onlyOnProduction &&
    import.meta.env.VITE_APP_ENVIRONMENT !== "production"
  ) {
    return children ? children : <Outlet />;
  }

  if (!config) {
    return null;
  }

  const redirectUrl = resolvePortalUrl(
    config.links?.[type as keyof typeof config.links]
  );

  if (redirectUrl) {
    window.location.replace(redirectUrl);
    return null;
  }

  return children ? children : <Outlet />;
};

export const MandatorFeatureRoute = ({
  children,
  feature,
  redirectTo = "/",
}: {
  children?: React.ReactNode;
  feature: keyof MandatorFeatures;
  redirectTo?: string;
}): React.ReactNode => {
  const isEnabled = useMandatorFeature(feature);

  if (!isEnabled) {
    return <Navigate to={redirectTo} replace />;
  }

  return children ? children : <Outlet />;
};

/**
 * Renders the route only in non-production environments. In production these
 * routes (auth/onboarding flows handled externally) redirect to the mandator
 * login URL, or to its registration URL when `target="registration"` and the
 * mandator configures one.
 */
export const NonProdRoute = ({
  children,
  target = "login",
}: {
  children?: React.ReactNode;
  target?: "login" | "registration";
}): React.ReactNode => {
  const { loginRedirectUrl, registrationRedirectUrl } = useLoginRoute();
  const isNonDev = import.meta.env.VITE_APP_ENVIRONMENT === "production";
  const redirectUrl =
    target === "registration" ? registrationRedirectUrl : loginRedirectUrl;

  useEffect(() => {
    if (isNonDev) {
      window.location.replace(redirectUrl);
    }
  }, [isNonDev, redirectUrl]);

  if (isNonDev) {
    return null;
  }

  return children ? children : <Outlet />;
};

interface WithComponentByMandatorProps {
  default: JSX.Element;
  [key: string]: JSX.Element;
}

export const WithComponentByMandator = (
  props: WithComponentByMandatorProps
) => {
  const { mandatorSlug } = useMandatorContext();

  if (!mandatorSlug) {
    return null;
  }

  return props[mandatorSlug]
    ? (props[mandatorSlug] as JSX.Element)
    : props["default"];
};

export const WithAppLayout = ({
  children,
}: {
  children?: React.ReactNode;
}): React.ReactNode => {
  return (
    <OnlyAuthenticatedRoute>
      <AppLayoutProvider>
        <AppLayout>{children}</AppLayout>
      </AppLayoutProvider>
    </OnlyAuthenticatedRoute>
  );
};

export const withSuspense = (
  Component: React.LazyExoticComponent<() => JSX.Element>
) => (
  <Suspense fallback={<LoadingProgressBar />}>
    <Component />
  </Suspense>
);
